/*
 * Free Samples picker — Devuchi #215 / Trello 1226
 *
 * Adds and removes complimentary sample packettes from the cart, then lets
 * the theme's existing cart plumbing re-render. The allowance also has to
 * track changes made elsewhere on the page (quantity edits, line removals),
 * so cart.js calls `refreshFreeSamples()` after its own update.
 *
 * NOTE: this section is deliberately NOT in cart.js `getSectionsToRender()`.
 * Shopify caps the `sections=` request at five sections and returns none at
 * all when that is exceeded, which froze the cart totals.
 */
class FreeSamples extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick.bind(this));
  }

  get remaining() {
    return parseInt(this.dataset.remaining, 10) || 0;
  }

  onClick(event) {
    const addButton = event.target.closest('[data-free-sample-add]');
    const removeButton = event.target.closest('[data-free-sample-remove]');
    if (!addButton && !removeButton) return;

    event.preventDefault();
    const button = addButton || removeButton;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    if (addButton && this.remaining <= 0) return;

    this.setBusy(true);
    this.clearError();

    const request = addButton ? this.addSample(variantId) : this.removeSample(variantId);

    request
      .then((state) => {
        if (state && state.status) {
          // Shopify returns a `status` field on errors (e.g. sold out).
          this.showError(state.description || state.message || this.defaultErrorMessage());
          return;
        }
        this.refreshCart(variantId);
      })
      .catch(() => {
        this.showError(this.defaultErrorMessage());
      })
      .finally(() => {
        this.setBusy(false);
      });
  }

  addSample(variantId) {
    // The `_free_sample` property marks the line as a Partner Rewards
    // sample. The leading underscore hides it from the customer-facing
    // cart and the order confirmation, but it stays visible on the order
    // in admin so fulfilment (and any discount automation) can identify
    // which lines were claimed as free samples rather than purchased.
    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1, properties: { _free_sample: 'Partner Rewards' } }],
      sections: this.sectionsToRender(),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } }).then((response) =>
      response.json()
    );
  }

  removeSample(variantId) {
    // `updates` sets an absolute quantity, so 0 removes the line entirely
    // regardless of how many were added.
    const body = JSON.stringify({
      updates: { [variantId]: 0 },
      sections: this.sectionsToRender(),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then((response) =>
      response.json()
    );
  }

  /*
   * Sections to re-render after adding or removing a sample.
   *
   * Shopify rejects the whole cart request once `sections=` names more than
   * FIVE sections — it returns an empty `sections` object rather than an error,
   * which leaves the line totals, subtotal and Staff Order box frozen. The
   * cart's own list already uses all five slots, so this asks ONLY for the
   * picker and lets `cart-items` repaint the rest via its own update below.
   */
  sectionsToRender() {
    return ['cart-free-samples'];
  }

  /*
   * Re-fetch the cart page and swap in the freshly rendered sections. This
   * reuses the theme's own cart-items element when present so the line-item
   * table, subtotal and this picker all stay in sync from one round trip.
   */
  refreshCart(variantId) {
    const cartItems = document.querySelector('cart-items');

    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      // Let the theme repaint the line items / footer, then refresh this
      // section so the allowance reflects the new cart contents.
      cartItems.onCartUpdate();
    }

    return fetch(`${window.location.pathname}?section_id=cart-free-samples`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('.cart-free-samples-wrapper');
        const destination = document.querySelector('.cart-free-samples-wrapper');
        if (source && destination) destination.innerHTML = source.innerHTML;

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-samples', variantId });
        }
      });
  }

  setBusy(isBusy) {
    this.classList.toggle('cart-free-samples--busy', isBusy);
    this.querySelectorAll('button[data-free-sample-add], button[data-free-sample-remove]').forEach(
      (button) => {
        if (isBusy) {
          button.setAttribute('aria-busy', 'true');
          button.disabled = true;
        } else {
          button.removeAttribute('aria-busy');
          // Re-enabling is left to the re-rendered markup, which knows the
          // current allowance; only unblock when nothing replaced us.
          if (this.isConnected && !this.dataset.rerendered) button.disabled = false;
        }
      }
    );
  }

  // Translated "Something went wrong…" text, rendered onto the error element
  // by sections/cart-free-samples.liquid.
  defaultErrorMessage() {
    const target = this.querySelector('[data-free-samples-error]');
    return (target && target.dataset.defaultMessage) || '';
  }

  showError(message) {
    const target = this.querySelector('[data-free-samples-error]');
    if (!target) return;
    target.textContent = message || '';
    target.hidden = !message;
  }

  clearError() {
    this.showError('');
  }
}

if (!customElements.get('free-samples')) {
  customElements.define('free-samples', FreeSamples);
}
